import { Description, Field, Input, Label } from '@headlessui/react'
import { cx } from 'class-variance-authority'
import React, { useState, HTMLInputTypeAttribute } from 'react'
import { EyeSlashIcon, EyeIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { ErrorMessage } from '../ErrorMessage'

interface NewInputProps {
  description?: string
  label?: string
  disabled?: boolean
  type?: HTMLInputTypeAttribute
  placeholder?: string
  name: string
  required?: boolean
  value?: HTMLInputElement['value']
  // eslint-disable-next-line no-unused-vars
  onChange: (value: HTMLInputElement['value']) => void
  error?: string
  IconInput?: React.ReactNode
  autoComplete?: HTMLInputElement['autocomplete']
}

export const NewInput = (props: NewInputProps) => {
  const {
    label,
    name,
    description,
    disabled = false,
    type = 'text',
    placeholder,
    required = true,
    value,
    onChange,
    error,
    IconInput,
    autoComplete = 'off',
  } = props
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [inputType, setInputType] = useState<NewInputProps['type']>(type)

  return (
    <Field disabled={disabled} aria-label={label} className="space-y-2">
      {label && (
        <Label className="font-medium text-night text-sm/6">{label}</Label>
      )}
      {description && (
        <Description className="font-thin text-gray-500 text-sm/6">
          {description}
        </Description>
      )}

      <div className="relative">
        {type !== 'password' && IconInput}
        <Input
          {...props}
          id={name}
          data-testid={name}
          name={name}
          disabled={disabled}
          data-invalid={Boolean(!isDirty || error)}
          type={inputType}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          className={cx(
            'flex flex-row items-center w-full gap-2 p-3 px-3 bg-white border-2 border-gray-200 rounded-full h-9 disabled:cursor-not-allowed placeholder:text-sm text-sm focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 text-night',
            Boolean(error) && 'border-red-300',
            Boolean(IconInput) && 'pl-10',
          )}
          value={value}
          onChange={(e) => {
            if (!isDirty) {
              setIsDirty(true)
            }
            if (onChange) onChange(e.target.value)
          }}
        />

        {type !== 'password' && (
          <button
            type="button"
            className="absolute top-2 right-3"
            onClick={() => {
              if (onChange) onChange('')
            }}
          >
            <span aria-label="bouton pour effacer">
              <XMarkIcon className="w-5 h-5 text-night" />
            </span>
          </button>
        )}

        {type === 'password' && (
          <button
            type="button"
            onClick={() =>
              setInputType(inputType === 'password' ? 'text' : 'password')
            }
            className="absolute top-2 right-3"
          >
            {inputType === 'password' && (
              <EyeSlashIcon className="w-5 h-5 text-night" />
            )}
            {inputType === 'text' && <EyeIcon className="w-5 h-5 text-night" />}
          </button>
        )}
      </div>
      <ErrorMessage error={error} data-testid={`${name}-error`} />
    </Field>
  )
}
